import { Injectable, inject } from '@angular/core';
import { FileUpload } from '../types/interfaces/file-upload';
import { BehaviorSubject, Subject } from 'rxjs';
import { MediaService } from './media.service';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { ChatMessage, WhatsAppUploadFailureResponse } from '../types/interfaces/message';
import { v4 } from 'uuid';
import {
  DashboardMediaUploadFailureReport,
  logFrontendHttpEvent,
} from './http-logging';

@Injectable({
  providedIn: 'root',
})
export class MediaUploadService {
  private mediaService = inject(MediaService);

  private pendingUploads: FileUpload[] = [];

  public pendingUploads$ = new BehaviorSubject<FileUpload[]>([]);
  readonly providerSendFailed$ = new Subject<ChatMessage>();

  appendPendingUpload(upload: FileUpload) {
    this.pendingUploads.push(upload);
    this.pendingUploads$.next(this.pendingUploads);
  }

  clearPendingUploads() {
    this.pendingUploads = [];
    this.pendingUploads$.next(this.pendingUploads);
  }

  queueUploadById(uploadId: string, messageId: number, chatId?: number) {
    const upload = this.pendingUploads.find((x) => x.tempId == uploadId);

    if (!upload || !upload._file) {
      return;
    }

    upload.messageId = messageId;
    if (chatId !== undefined) upload.chatId = chatId;
    if (upload.status !== 'waiting') return;

    const uploading = this.pendingUploads.some((x) => x.status == 'uploading');

    if (uploading) {
      return;
    }

    this.setupUpload(upload);
  }

  retryUploadById(uploadId: string, messageId: number, chatId?: number) {
    const upload = this.pendingUploads.find((x) => x.tempId === uploadId);
    if (!upload || upload.status !== 'error') return;
    upload.status = 'waiting';
    upload.progress = 0;
    this.queueUploadById(uploadId, messageId, chatId);
  }

  reportClientRejection(
    file: File,
    chatId: number | undefined,
    errorCode: string,
    errorMessage: string,
  ) {
    const requestId = v4();
    const tempId = v4();
    const report: DashboardMediaUploadFailureReport = {
      eventType: 'client_rejection',
      requestId,
      ...(chatId === undefined ? {} : { chatId }),
      tempId,
      fileSize: file.size,
      mimeType: file.type,
      failureType: 'validation',
      errorCode,
      errorMessage,
    };
    logFrontendHttpEvent({
      event: 'dashboard.media_upload.rejected',
      requestId,
      method: 'LOCAL',
      endpoint: 'chat/file-picker',
      feature: 'dashboard-media-upload',
      ...(chatId === undefined ? {} : { chatId }),
      tempId,
      fileSize: file.size,
      mimeType: file.type,
      failureType: 'validation',
      errorCode,
      errorMessage,
    });
    this.submitFailureReport(report);
  }

  private setupUpload(upload: FileUpload) {
    const requestId = v4();
    const attempt = upload.attempt = (upload.attempt ?? 0) + 1;
    const endpoint = `/api/v1/media/${upload.messageId}`;
    const startedAt = performance.now();
    const logFields = {
      requestId,
      method: 'POST',
      endpoint,
      feature: 'dashboard-media-upload',
      messageId: upload.messageId!,
      ...(upload.chatId === undefined ? {} : { chatId: upload.chatId }),
      tempId: upload.tempId,
      attempt,
      fileSize: upload._file!.size,
      mimeType: upload._file!.type,
    };
    logFrontendHttpEvent({ ...logFields, event: 'http.request.started' });
    upload.status = 'uploading';
    this.pendingUploads$.next([...this.pendingUploads]);

    this.mediaService.uploadFile(upload._file!, upload.messageId!, requestId).subscribe({
      next: (event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = event.total
              ? Math.round((100 * event.loaded) / event.total)
              : 0;
            upload.progress = progress;
            this.pendingUploads$.next([...this.pendingUploads]);
            break;
          case HttpEventType.Response:
            upload.url = event.body.url;
            upload.progress = 100;
            upload.status = 'uploaded';
            logFrontendHttpEvent({
              ...logFields,
              event: 'http.request.completed',
              statusCode: event.status,
              durationMs: Math.round(performance.now() - startedAt),
              progress: 100,
            });
            this.pendingUploads$.next([...this.pendingUploads]);
            break;
        }
      },
      error: (error: unknown) => {
        const statusCode = (error as { status?: unknown })?.status;
        const providerFailure = this.handleProviderSendFailure(upload, error);
        const response = error as {
          message?: unknown;
          error?: {
            code?: unknown;
            message?: unknown;
            chatMessage?: ChatMessage;
          };
        };
        const deliveryFailure = response.error?.chatMessage?.metadata?.whatsappDelivery?.failure;
        const providerError = deliveryFailure?.errors?.[0];
        const failureType = providerFailure || response.error?.code === 'WHATSAPP_SEND_FAILED'
          ? 'provider'
          : statusCode === 0
            ? 'network'
            : 'http';
        const errorCode = providerError?.code !== undefined
          ? String(providerError.code)
          : typeof response.error?.code === 'string'
            ? response.error.code
            : undefined;
        const errorMessage = deliveryFailure?.message ??
          providerError?.details ??
          (typeof response.error?.message === 'string' ? response.error.message : undefined) ??
          (typeof response.message === 'string' ? response.message : undefined);
        const durationMs = Math.round(performance.now() - startedAt);
        logFrontendHttpEvent({
          ...logFields,
          event: 'http.request.failed',
          ...(typeof statusCode === 'number' ? { statusCode } : {}),
          failureType,
          durationMs,
          progress: upload.progress,
          ...(errorCode ? { errorCode } : {}),
          ...(errorMessage ? { errorMessage } : {}),
        });
        this.submitFailureReport({
          eventType: 'upload_failure',
          requestId,
          messageId: upload.messageId!,
          ...(upload.chatId === undefined ? {} : { chatId: upload.chatId }),
          tempId: upload.tempId,
          attempt,
          fileSize: upload._file!.size,
          mimeType: upload._file!.type,
          ...(typeof statusCode === 'number' ? { statusCode } : {}),
          failureType,
          durationMs,
          progress: upload.progress,
          ...(errorCode ? { errorCode } : {}),
          ...(errorMessage ? { errorMessage } : {}),
        });
        if (providerFailure) return;
        upload.status = 'error';
        this.pendingUploads$.next([...this.pendingUploads]);

        this.handleNextUpload();
      },
      complete: () => {
        this.handleNextUpload();
      },
    });
  }

  private clearCompletedUploads() {
    this.pendingUploads = this.pendingUploads.filter(
      (x) => x.status != 'uploaded'
    );
    this.pendingUploads$.next([...this.pendingUploads]);
  }

  private handleNextUpload() {
    if (this.pendingUploads.some(upload => upload.status === 'uploading')) return;
    const next = this.pendingUploads.find(
      (x) =>
        x.status === 'waiting' &&
        Boolean(x.messageId) &&
        Boolean(x._file)
    );

    if (next) {
      this.setupUpload(next);
    }

    this.clearCompletedUploads();
  }

  private handleProviderSendFailure(upload: FileUpload, error: unknown): boolean {
    const response = error as { status?: number; error?: Partial<WhatsAppUploadFailureResponse> };
    const body = response?.error;
    const message = body?.chatMessage;
    const valid = response?.status === 502 &&
      body?.code === 'WHATSAPP_SEND_FAILED' &&
      body.messageId === upload.messageId &&
      typeof body.url === 'string' && body.url.length > 0 &&
      typeof body.message === 'string' &&
      !!message &&
      message.id === upload.messageId &&
      message.tempId === upload.tempId &&
      message.type === 'media' &&
      message.idStatus === 7 &&
      Array.isArray(message.media) &&
      message.media.some(media => media.url === body.url);
    if (!valid || message?.type !== 'media') return false;

    this.pendingUploads = this.pendingUploads.filter(item => item.tempId !== upload.tempId);
    this.pendingUploads$.next([...this.pendingUploads]);
    this.providerSendFailed$.next(message);
    this.handleNextUpload();
    return true;
  }

  private submitFailureReport(report: DashboardMediaUploadFailureReport) {
    const request = this.mediaService.reportUploadFailure?.(report);
    request?.subscribe({
      error: (error: unknown) => {
        const response = error as { status?: unknown; message?: unknown };
        const statusCode = typeof response?.status === 'number' ? response.status : undefined;
        logFrontendHttpEvent({
          event: 'dashboard.media_upload.report.failed',
          requestId: report.requestId,
          method: 'POST',
          endpoint: '/api/v1/logs/frontend/media-upload-failure',
          feature: 'dashboard-media-upload',
          ...(report.messageId === undefined ? {} : { messageId: report.messageId }),
          ...(report.chatId === undefined ? {} : { chatId: report.chatId }),
          tempId: report.tempId,
          ...(statusCode === undefined ? {} : { statusCode }),
          failureType: statusCode === 0 ? 'network' : 'http',
          errorCode: 'MEDIA_UPLOAD_REPORT_FAILED',
          ...(typeof response?.message === 'string' ? { errorMessage: response.message } : {}),
        });
      },
    });
  }
}
